import html2canvas from 'html2canvas';
import i18n from '@/i18n';
import { scpmUtil } from '@/utils/scpmUtil.js';
import { useDateTimeFormat } from '@/composables/useDateTimeFormat';
import { IGNORE_ELEMENTS } from '@/containers/utils/constant';

// eslint-disable-next-line no-useless-escape
const EXCEPT_SPECIAL_CHAR = /[~`!@#$%^&*()+={}\[\];:\'\"<>.,\/\\\?-]/g;
// format-date
const { formatDateTime, FULL_TIME_FORMAT } = useDateTimeFormat();
const generateDownloadFileName = function (rawData, suffix = '') {
  // rawData 는 /v1/console/metric-data 의 응답 데이터(contents)

  // 파일 이름 생성: "모니터링 대상명_성능 항목명_(개별항목명)_YYYYMMDDhhmm"
  let prodNames = [];
  let prodName;
  let eachNames = [];
  let eachName;
  let found;
  let first = 1;
  let perfName;

  rawData.forEach((raw, index) => {
    if (raw.productName) {
      found = 0;
      for (let i = 0; i < prodNames.length; i++) {
        if (prodNames[i] == raw.productName) {
          found = 1;
          break;
        }
      }
      if (found == 0) {
        prodNames.push(raw.productName);
        if (first == 1) {
          prodName = raw.productName;
        }
      }
    }

    if (raw.objectDisplayName) {
      found = 0;
      for (let i = 0; i < eachNames.length; i++) {
        if (eachNames[i] == raw.objectDisplayName) {
          found = 1;
          break;
        }
      }
      if (found == 0) {
        eachNames.push(raw.objectDisplayName);
        if (first == 1) {
          eachName = raw.objectDisplayName;
        }
      }
    }

    if (first == 1) {
      if (raw.metricName) {
        perfName = raw.metricName.replace(EXCEPT_SPECIAL_CHAR, '').replace(/\s/g, '_');
      } else perfName = raw.metricKey;

      first = 0;
    }
  });

  // 파일 이름: "모니터링 대상명_성능 항목명_(개별항목명)_YYYYMMDDhhmm"
  let _other = i18n.global.t('label.other').trim();
  let productName =
    (prodName ?? '') + (prodNames.length > 1 ? _other + (prodNames.length - 1) : '');
  let fileName = productName ? `${productName}_` : '';
  fileName += perfName ? `${perfName}_` : '';
  if (eachNames.length > 0) {
    const eachNameStr =
      (eachName ?? '') + (eachNames.length > 1 ? _other + (eachNames.length - 1) : '');

    fileName += eachNameStr ? `${eachNameStr}_` : '';
  }
  fileName += formatDateTime().replace(/[-: "]/g, '');
  fileName += suffix;
  return fileName;
};

const downloadMetricDataImage = function (rawData, chartElement) {
  html2canvas(chartElement, {
    ignoreElements: function (el) {
      if (IGNORE_ELEMENTS.includes(el.className)) {
        return true;
      }
    }
  }).then((canvas) => {
    const link = document.createElement('a');

    let fileName = generateDownloadFileName(rawData);
    link.setAttribute('download', fileName + '.png');
    link.setAttribute(
      'href',
      canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream')
    );
    link.click();
  });
};

const downloadMetricDataExcel = function (startDt, endDt, rawData) {
  const xlsx = require('xlsx');
  const dataPeriod = getFormatSearchPreiod(startDt, endDt);

  let excelDatas = [];
  excelDatas.push([dataPeriod]);
  excelDatas.push([]);
  excelDatas.push([
    i18n.global.t('excel.metricItem'),
    i18n.global.t('excel.ts'),
    i18n.global.t('excel.perfValue'),
    i18n.global.t('excel.productName'),
    i18n.global.t('excel.unit')
  ]);

  rawData.forEach((raw) => {
    for (const rawSub of raw.data) {
      const [time, val] = rawSub;
      if (val === null) continue;
      const ts = formatDateTime(time, FULL_TIME_FORMAT);
      const row = [raw.metricName, ts, val, raw.name, raw.unit];
      excelDatas.push(row);
    }
  });

  const workSheet = xlsx.utils.aoa_to_sheet(excelDatas);

  // chell style
  workSheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 4 } }];
  workSheet['!rows'] = [{ hpx: 40 }];
  workSheet['!cols'] = [{ wch: 50 }, { wch: 20 }, { wch: 10 }, { wch: 65 }, { wch: 5 }];

  // A1
  workSheet['A1'].s = {
    alignment: {
      vertical: 'center',
      horizontal: 'center'
    },
    font: {
      sz: 15,
      bold: true,
      color: { rgb: 'FFFF0000' }
    }
  };

  customStyleHeader(workSheet, 5, 10);
  const workBook = xlsx.utils.book_new();
  xlsx.utils.book_append_sheet(workBook, workSheet, 'perf_data');
  const fileName = `${generateDownloadFileName(rawData)}.xlsx`;
  xlsx.writeFile(workBook, fileName);
};

/**
 * Function save file raw metric data excel
 * @startDt {Datetime} - download data from start date on search period
 * @rawData {Object} - raw data from response api metric data
 * @endDt {Datetime} - download data from end date on search period
 * */
const downloadRawMetricDataExcel = function (startDt, endDt, rawData) {
  const xlsx = require('xlsx');
  const dataPeriod = getFormatSearchPreiod(startDt, endDt);
  let excelDatas = [];
  excelDatas.push([dataPeriod]);
  excelDatas.push([]);
  excelDatas.push([
    i18n.global.t('excel.metricItem'),
    i18n.global.t('excel.ts'),
    i18n.global.t('excel.perfValue'),
    i18n.global.t('excel.productName'),
    i18n.global.t('excel.unit')
  ]);
  const _unit = rawData[0].metricUnit;
  rawData.forEach((raw) => {
    const _objName = raw.objectDisplayName;
    const _productName = raw.productName + (_objName === null ? '' : '.' + _objName);
    const _staticName = '';
    // raw.statisticsType
    //   ? '(' + i18n.global.t(`label.${raw.statisticsType.toLowerCase()}`) + ')'
    //   : '';
    const _metric = raw.metricName;
    raw.perfData.forEach((rawSub) => {
      const { ts, value } = rawSub;
      const t = formatDateTime(ts, FULL_TIME_FORMAT);
      let row = [_metric, t, value, _productName + _staticName, _unit];
      excelDatas.push(row);
    });
  });
  const workSheet = xlsx.utils.aoa_to_sheet(excelDatas);
  workSheet['!cols'] = [{ wch: 50 }, { wch: 20 }, { wch: 10 }, { wch: 65 }, { wch: 5 }];
  workSheet['!rows'] = [{ hpx: 40 }];
  workSheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 4 } }];
  workSheet['A1'].s = {
    alignment: {
      vertical: 'center',
      horizontal: 'center'
    },
    font: {
      sz: 15,
      bold: true,
      color: { rgb: 'FFFF0000' }
    }
  };

  customStyleHeader(workSheet, 5, 10);
  const workBook = xlsx.utils.book_new();
  xlsx.utils.book_append_sheet(workBook, workSheet, 'perf_data');
  const fileName = `${generateDownloadFileName(rawData, '_raw')}.xlsx`;
  xlsx.writeFile(workBook, fileName);
};

const downloadEventStatusExcel = (
  rawData,
  searchPeriod,
  isCustomDashboard,
  widgetName,
  eventState
) => {
  const xlsx = require('xlsx');
  const dataPeriod = getFormatSearchPreiod(searchPeriod.queryStartDt, searchPeriod.queryEndDt);
  let excelDatas = [];
  excelDatas.push([dataPeriod]);
  excelDatas.push([]);
  excelDatas.push([
    i18n.global.t('excel.eventMessage'),
    i18n.global.t('excel.eventState'),
    i18n.global.t('excel.eventLevel'),
    i18n.global.t('excel.occurrenceDate')
  ]);
  rawData.forEach((raw) => {
    const { eventMessage, eventState, eventLevel, startDt } = raw;
    const occurrenceDate = formatDateTime(startDt, FULL_TIME_FORMAT);
    const row = [eventMessage, eventState, eventLevel, occurrenceDate];
    excelDatas.push(row);
  });
  const workSheet = xlsx.utils.aoa_to_sheet(excelDatas);

  // // chell style
  workSheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 4 } }];
  workSheet['!cols'] = [{ wch: 100 }, { wch: 20 }, { wch: 20 }, { wch: 30 }];
  workSheet['!rows'] = [{ hpx: 40 }];
  // // A1
  workSheet['A1'].s = {
    alignment: {
      vertical: 'center',
      horizontal: 'center'
    },
    font: {
      sz: 15,
      bold: true,
      color: { rgb: 'FFFF0000' }
    }
  };

  customStyleHeader(workSheet, 4, 12);
  const workBook = xlsx.utils.book_new();
  xlsx.utils.book_append_sheet(workBook, workSheet, 'perf_data');
  const dateNow = formatDateTime().replace(/[-: "]/g, '');
  const fileName = isCustomDashboard
    ? `${widgetName}_${eventState}_${dateNow}.xlsx`
    : `${i18n.global.t('title.eventStatus')}_${eventState}_${dateNow}.xlsx`;
  xlsx.writeFile(workBook, fileName);
};

const customStyleHeader = (workSheet, cols, fontSize) => {
  for (let i = 0; i < cols; i++) {
    workSheet[String.fromCharCode(65 + i) + '3'].s = {
      alignment: {
        vertical: 'center',
        horizontal: 'center'
      },
      fill: {
        fgColor: { rgb: 'FF0000FF' }
      },
      font: {
        sz: fontSize,
        bold: true,
        color: { rgb: 'FFFFFFFF' }
      }
    };
  }
};

const getFormatSearchPreiod = (startDate, endDate) => {
  const startTime = formatDateTime(startDate, FULL_TIME_FORMAT);
  const endTime = formatDateTime(endDate, FULL_TIME_FORMAT);
  const dataPeriod = `${i18n.global.t('excel.searchPeriod')}: ${startTime} ~ ${endTime}`;
  return dataPeriod;
};

export {
  downloadMetricDataImage,
  downloadMetricDataExcel,
  downloadRawMetricDataExcel,
  downloadEventStatusExcel,
  getFormatSearchPreiod,
  customStyleHeader
};
